import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AccountTreeRoundedIcon from '@mui/icons-material/AccountTreeRounded';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminDashboardBffApi, type DecisionEngineJourneyDto } from '../../api/admin';
import type { ApplicationTabKey } from '../../lib/applicationDetailTabStatus';
import {
  coalesceDecisionDeclineCopy,
  isDecisionEngineHardDecline,
} from '../../lib/decisionEngineDeclineCopy';
import {
  buildPdlCapacityRateKvRows,
  isPdlLoanType,
} from '../../lib/pdlInterestRateDisplay';
import {
  buildPdlElodRiskRows,
  buildPdlGroupedTraceSections,
  pdlElodHasRiskSignals,
} from '../../lib/pdlDecisionPanelRows';
import { buildPdlRemarkCards, groupPdlRemarkCards } from '../../lib/pdlRemarkPresentation';
import { ApplicationSectionCard } from '../application/applicationSectionChrome';
import { extractCalculationTransactionsFromDetail } from '../../lib/incomeCalculationDerivation';
import { buildPdlDecisionEvidence } from '../../lib/pdlDecisionEvidence';
import { PDL_PANEL_SUBTITLE } from '../../lib/pdlAdjudicatorUiCopy';
import { usePdlDecisionTourOptional } from './guidedTour/PdlDecisionTourContext';
import { PdlAdjudicatorBrief } from './PdlAdjudicatorBrief';
import { PdlEligibilityHelpButton } from './PdlEligibilityHelpButton';
import { PdlPolicySignalsPanel } from './PdlPolicySignalsPanel';
import { PdlTechnicalTracePanel } from './PdlTechnicalTracePanel';
import { resolvePdlDecisionStubPack } from '../../dev/pdlDecisionEngineStubs';

function latestIncomeRecordId(incomeRecords: unknown): string | null {
  if (!incomeRecords || typeof incomeRecords !== 'object' || Array.isArray(incomeRecords)) {
    return null;
  }
  const items = (incomeRecords as { items?: unknown }).items;
  if (!Array.isArray(items) || items.length === 0) return null;
  const first = items[0];
  if (!first || typeof first !== 'object' || Array.isArray(first)) return null;
  const id = (first as { id?: unknown }).id;
  return typeof id === 'string' && id.trim() ? id.trim() : null;
}

function incomeMetricsFromRecords(incomeRecords: unknown): unknown {
  if (!incomeRecords || typeof incomeRecords !== 'object' || Array.isArray(incomeRecords)) {
    return null;
  }
  const items = (incomeRecords as { items?: unknown }).items;
  if (!Array.isArray(items) || items.length === 0) return null;
  const first = items[0];
  if (!first || typeof first !== 'object' || Array.isArray(first)) return null;
  return (first as { metrics?: unknown }).metrics ?? null;
}

type Props = {
  decisionEngine?: DecisionEngineJourneyDto | null;
  /** Journey income verification list payload (same shape as Income tab). */
  incomeRecords?: unknown | null;
  /** Journey product type — fallback when DE productCode is missing. */
  productType?: string | null;
  /** Persisted applications.decline_reason (ops source of truth for customer hard-stop). */
  applicationDeclineReason?: string | null;
  /** Journey status — used so APPROVE + echoed "CA-Approved" is not treated as a hard stop. */
  applicationStatus?: string | null;
  onNavigate?: (key: ApplicationTabKey) => void;
};

type AddressLike = {
  street?: string | null;
  city?: string | null;
  province?: string | null;
  postal?: string | null;
  dateReported?: string | null;
};

function decisionChip(decision?: string) {
  const upper = (decision ?? '').toUpperCase();
  if (upper === 'APPROVE') {
    return (
      <Chip
        icon={<CheckCircleOutlineRoundedIcon />}
        size="small"
        color="success"
        label="Approve"
        sx={{ height: 22, fontWeight: 700 }}
      />
    );
  }
  if (upper === 'DECLINE') {
    return (
      <Chip
        icon={<ErrorOutlineRoundedIcon />}
        size="small"
        color="error"
        label="Decline"
        sx={{ height: 22, fontWeight: 700 }}
      />
    );
  }
  if (upper === 'REFER') {
    return (
      <Chip
        icon={<InfoOutlinedIcon />}
        size="small"
        color="warning"
        label="Refer"
        sx={{ height: 22, fontWeight: 700 }}
      />
    );
  }
  if (!upper) return null;
  return <Chip size="small" variant="outlined" label={upper} sx={{ height: 22, fontWeight: 700 }} />;
}

function formatMoney(value?: number | null) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0,
  }).format(value);
}

function formatPercent(value?: number | null) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  if (Math.abs(value) <= 1) return `${(value * 100).toFixed(1)}%`;
  return `${value}%`;
}

function formatChance(value?: number | null) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  if (Math.abs(value) <= 1) return `${(value * 100).toFixed(1)}%`;
  return String(value);
}

function formatYesNo(value?: boolean | null) {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  return null;
}

/** TransUnion DateOpened is raw YYYYMMDD — render as YYYY-MM-DD (leave anything else as-is). */
function formatTuDate(value?: string | null): string {
  const v = typeof value === 'string' ? value.trim() : '';
  if (!v) return '—';
  const m = v.match(/^(\d{4})(\d{2})(\d{2})$/);
  return m ? `${m[1]}-${m[2]}-${m[3]}` : v;
}

/** TransUnion MOP / account rating code → human label (code kept for the analyst). */
const TU_STATUS_LABELS: Record<string, string> = {
  '0': 'Too new',
  '1': 'Current',
  '2': '30d late',
  '3': '60d late',
  '4': '90d late',
  '5': '120d+ late',
  '7': 'Consumer proposal',
  '8': 'Repossession',
  '9': 'Collection / bad debt',
};
function formatTuStatus(value?: string | null): string {
  const v = typeof value === 'string' ? value.trim() : '';
  if (!v) return '—';
  const label = TU_STATUS_LABELS[v];
  return label ? `${v} · ${label}` : v;
}

function formatAddress(addr?: AddressLike | null) {
  if (!addr) return null;
  const line = [addr.street, addr.city, addr.province, addr.postal]
    .map((p) => (typeof p === 'string' ? p.trim() : ''))
    .filter(Boolean)
    .join(', ');
  if (!line) return null;
  if (addr.dateReported?.trim()) return `${line} (reported ${addr.dateReported.trim()})`;
  return line;
}

type KvField = {
  label: string;
  value?: string | number | null;
  emphasize?: boolean;
  /** When true, still render the row with "—" if value is empty. */
  always?: boolean;
};

function visibleKvRows(rows: KvField[]): KvField[] {
  return rows
    .map((r) => {
      if (r.value != null && r.value !== '') return r;
      if (r.always) return { ...r, value: '—' };
      return null;
    })
    .filter((r): r is KvField => r != null);
}

function KvSection({
  title,
  subtitle,
  rows,
}: {
  title: string;
  subtitle?: string;
  rows: KvField[];
}) {
  const visible = visibleKvRows(rows);
  if (visible.length === 0) return null;

  return (
    <Box
      sx={{
        minWidth: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.85,
        p: 1.5,
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Box>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 800,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            color: 'text.primary',
            fontSize: '0.65rem',
            display: 'block',
          }}
        >
          {title}
        </Typography>
        {subtitle ? (
          <Typography
            variant="caption"
            display="block"
            sx={{ mt: 0.2, lineHeight: 1.4, color: 'text.primary', fontWeight: 500, opacity: 0.78 }}
          >
            {subtitle}
          </Typography>
        ) : null}
      </Box>
      <Box component="dl" sx={{ m: 0, flex: 1 }}>
        {visible.map((row, i) => (
          <Box
            key={row.label}
            sx={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              columnGap: 2,
              rowGap: 0.25,
              py: 0.8,
              borderTop: i === 0 ? undefined : '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography
              component="dt"
              sx={{
                flex: '1 1 auto',
                minWidth: 0,
                fontSize: '0.8125rem',
                fontWeight: 600,
                color: 'text.primary',
                lineHeight: 1.4,
                opacity: 0.9,
              }}
            >
              {row.label}
            </Typography>
            <Typography
              component="dd"
              variant="body2"
              sx={{
                m: 0,
                flex: '0 1 auto',
                maxWidth: '58%',
                textAlign: 'right',
                fontWeight: row.emphasize && row.value !== '—' ? 700 : 600,
                fontSize: row.emphasize && row.value !== '—' ? '0.95rem' : '0.875rem',
                color: row.value === '—' ? 'text.secondary' : 'text.primary',
                lineHeight: 1.45,
                wordBreak: 'break-word',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {row.value}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function TableHeaderCell({ children, align }: { children: string; align?: 'right' | 'left' }) {
  return (
    <Typography
      component="th"
      variant="caption"
      sx={{
        fontWeight: 800,
        letterSpacing: '0.07em',
        textTransform: 'uppercase',
        color: 'text.secondary',
        fontSize: '0.65rem',
        textAlign: align ?? 'left',
        py: 1,
        px: 1.25,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </Typography>
  );
}

export function ApplicationDecisionEnginePanel({
  decisionEngine: decisionEngineProp,
  incomeRecords,
  productType,
  applicationDeclineReason: applicationDeclineReasonProp,
  applicationStatus,
  onNavigate,
}: Props) {
  const pdlStub =
    import.meta.env.DEV ? resolvePdlDecisionStubPack(import.meta.env.VITE_PDL_DECISION_STUB) : null;
  const decisionEngine = pdlStub
    ? { ...decisionEngineProp, ...pdlStub.decisionEngine }
    : decisionEngineProp;
  const applicationDeclineReason =
    pdlStub?.applicationDeclineReason ?? applicationDeclineReasonProp;

  const theme = useTheme();
  const pdlTour = usePdlDecisionTourOptional();
  const [localTechnicalOpen, setLocalTechnicalOpen] = useState(false);
  const [localRemarksOpen, setLocalRemarksOpen] = useState(false);
  const pdlTechnicalOpen = pdlTour?.technicalOpen ?? localTechnicalOpen;
  const setPdlTechnicalOpen = pdlTour?.setTechnicalOpen ?? setLocalTechnicalOpen;
  const pdlRemarksOpen = pdlTour?.remarksOpen ?? localRemarksOpen;
  const setPdlRemarksOpen = pdlTour?.setRemarksOpen ?? setLocalRemarksOpen;
  const isPdlEarly = isPdlLoanType({
    productCode: decisionEngine?.productCode,
    productType,
  });
  const latestIncomeId = useMemo(() => latestIncomeRecordId(incomeRecords ?? null), [incomeRecords]);
  const incomeDetailQ = useQuery({
    queryKey: ['pdlDecisionIncomeEvidence', latestIncomeId],
    queryFn: () => adminDashboardBffApi.incomeRecordDetail(latestIncomeId!),
    enabled: isPdlEarly && !!latestIncomeId,
    staleTime: 60_000,
  });
  const pdlEvidence = useMemo(
    () =>
      buildPdlDecisionEvidence({
        decisionEngine,
        incomeAudit: extractCalculationTransactionsFromDetail(incomeDetailQ.data),
        incomeMetrics: incomeMetricsFromRecords(incomeRecords ?? null),
      }),
    [decisionEngine, incomeDetailQ.data, incomeRecords],
  );

  if (!decisionEngine?.evaluationId && !decisionEngine?.decision && !applicationDeclineReason) {
    return (
      <Typography
        variant="body2"
        color="text.secondary"
        data-testid="application-decision-engine-empty"
      >
        No Decision Engine evaluation is on file for this application yet. DE runs after successful
        income verification.
      </Typography>
    );
  }

  const declined = isDecisionEngineHardDecline({
    decision: decisionEngine?.decision,
    applicationDeclineReason,
    applicationStatus,
  });

  const declineCopy = coalesceDecisionDeclineCopy({
    applicationDeclineReason,
    declinedReason: decisionEngine?.declinedReason,
    declinedExplanation: decisionEngine?.declinedExplanation,
  });
  const isPdl = isPdlLoanType({
    productCode: decisionEngine?.productCode,
    productType,
  });

  // PDL brief is the single decline surface — avoid triplicate banner + block + result.
  const showDeclineBlock =
    !isPdl && declined && Boolean(declineCopy.code || declineCopy.explanation);

  const applicantName = [decisionEngine?.applicantFirstName, decisionEngine?.applicantLastName]
    .filter(Boolean)
    .join(' ')
    .trim();

  const productBand =
    typeof decisionEngine?.offerMinAmount === 'number' ||
    typeof decisionEngine?.offerMaxAmount === 'number'
      ? [formatMoney(decisionEngine?.offerMinAmount), formatMoney(decisionEngine?.offerMaxAmount)]
          .filter(Boolean)
          .join(' – ')
      : null;

  const existingLoans = decisionEngine?.existingLoans ?? [];

  const pastAddress = formatAddress(decisionEngine?.pastAddress);
  const currentAddress = formatAddress(decisionEngine?.currentAddress);

  const pdl = decisionEngine?.pdlAffordability;

  const rateAndTermRows: KvField[] = isPdl
    ? buildPdlCapacityRateKvRows(decisionEngine?.interestRate)
    : [
        {
          label: 'Interest rate',
          value: formatPercent(decisionEngine?.interestRate),
          always: true,
        },
        {
          label: 'Max term',
          value:
            typeof decisionEngine?.maxTerm === 'number'
              ? `${decisionEngine.maxTerm} months`
              : null,
          always: true,
        },
      ];

  const capacityRows: KvField[] = [
    {
      label: 'Max eligible amount',
      value: formatMoney(decisionEngine?.maxEligibleAmount),
      emphasize: true,
      always: true,
    },
    // On hard decline, vendor may still emit Max_approved_amount — that is not capacity offered.
    ...(!declined
      ? [{ label: 'Approved amount', value: formatMoney(decisionEngine?.approvedAmount), always: true }]
      : []),
    { label: 'Requested amount', value: formatMoney(decisionEngine?.requestedAmount), always: true },
    ...rateAndTermRows,
    { label: 'Product', value: decisionEngine?.productCode, always: true },
    ...(declined
      ? [{ label: 'Product table band', value: productBand, always: true }]
      : [{ label: 'Product offer band', value: productBand, always: true }]),
  ];

  const logicRows: KvField[] = [
    {
      label: 'Ops product table max',
      value: formatMoney(decisionEngine?.productTableMaxAmount ?? decisionEngine?.offerMaxAmount),
      always: true,
    },
    {
      label: 'Lending amount (py model)',
      value: formatMoney(decisionEngine?.lendingAmount),
      always: true,
    },
    {
      label: 'DTI-based lending amount',
      value: formatMoney(decisionEngine?.dtiBasedLendingAmount),
      always: true,
    },
    { label: 'Chance of default', value: formatChance(decisionEngine?.chanceOfDefault), always: true },
    { label: 'Adj calib decision', value: decisionEngine?.adjCalibDecision, always: true },
    { label: 'Fin calib decision', value: decisionEngine?.finCalibDecision, always: true },
  ];

  const dtiRows: KvField[] = [
    { label: 'DTI', value: formatPercent(decisionEngine?.dtiRate), emphasize: true, always: true },
    {
      label: 'Monthly income (bureau)',
      value: formatMoney(decisionEngine?.monthlyIncome),
      always: true,
    },
    { label: 'Employer (TU)', value: decisionEngine?.employerName, always: true },
    { label: 'Occupation', value: decisionEngine?.occupation, always: true },
  ];

  const tuRows: KvField[] = [
    {
      label: 'Credit score',
      value: decisionEngine?.tuBureauScore ?? decisionEngine?.creditScore,
      emphasize: true,
      always: true,
    },
    { label: 'Bureau hit', value: decisionEngine?.tuHitStatus, always: true },
    { label: 'On-file date', value: decisionEngine?.tuOnFileDate, always: true },
    {
      label: 'Inquiries (6 months)',
      value:
        typeof decisionEngine?.inquiryCount6m === 'number' ? decisionEngine.inquiryCount6m : null,
      always: true,
    },
    {
      label: 'Active mortgage',
      value: formatYesNo(decisionEngine?.activeMortgageIndicator),
      always: true,
    },
    {
      label: 'Prior loan count',
      value:
        typeof decisionEngine?.previousLoanCount === 'number'
          ? decisionEngine.previousLoanCount
          : null,
      always: true,
    },
  ];

  const addressRows: KvField[] = [
    { label: 'Current address', value: currentAddress, always: true },
    { label: 'Past address (latest)', value: pastAddress, always: true },
  ];

  const pdlElodRows = buildPdlElodRiskRows(pdl);
  const pdlRemarkCards = buildPdlRemarkCards(pdl);
  const pdlRemarkGroups = groupPdlRemarkCards(pdlRemarkCards);
  const pdlTraceSections = buildPdlGroupedTraceSections(pdl);
  const pdlTraceRowCount = pdlTraceSections.reduce((n, s) => n + s.rows.length, 0);
  const showPdlElodDetail = !isPdl && pdlElodHasRiskSignals(pdl);

  const hasEvaluation = Boolean(decisionEngine?.evaluationId || decisionEngine?.decision);

  return (
    <ApplicationSectionCard
      title="Decision Engine"
      subtitle={
        isPdl
          ? PDL_PANEL_SUBTITLE
          : 'Capacity decision after income verification'
      }
      icon={<AccountTreeRoundedIcon fontSize="small" />}
      data-testid="application-decision-engine-panel"
      action={
        isPdl && (decisionEngine?.evaluationId || decisionEngine?.decision) ? (
          <PdlEligibilityHelpButton
            decisionEngine={decisionEngine}
            applicationDeclineReason={applicationDeclineReason}
          />
        ) : undefined
      }
      endAdornment={
        <>
          {decisionChip(decisionEngine?.decision)}
          {decisionEngine?.opLoanStatus ? (
            <Chip
              size="small"
              variant="outlined"
              label={decisionEngine.opLoanStatus}
              sx={{ height: 22, fontWeight: 700 }}
            />
          ) : null}
          {decisionEngine?.caStatus ? (
            <Chip
              size="small"
              variant="outlined"
              label={`CA: ${decisionEngine.caStatus}`}
              sx={{ height: 22, fontWeight: 700 }}
            />
          ) : null}
        </>
      }
    >
      <Stack spacing={1.5} data-tour-target="pdl-decision-panel">
        {applicantName ? (
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45 }}>
            {applicantName}
          </Typography>
        ) : null}

        {showDeclineBlock ? (
          <Box
            sx={{
              px: 1.5,
              py: 1.25,
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: alpha(theme.palette.error.main, 0.4),
              bgcolor: 'background.paper',
              borderLeftWidth: 3,
              borderLeftColor: 'error.main',
            }}
            data-testid="application-decision-engine-decline-reason"
          >
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 0.75 }}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 800,
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                  color: 'text.primary',
                  fontSize: '0.65rem',
                }}
              >
                Decline reason
              </Typography>
              {declineCopy.code ? (
                <Chip
                  size="small"
                  color="error"
                  variant="outlined"
                  label={declineCopy.code}
                  sx={{ height: 22, fontWeight: 800 }}
                />
              ) : null}
            </Stack>
            {declineCopy.explanation ? (
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, lineHeight: 1.5, color: 'text.primary' }}
                data-testid="application-decision-engine-decline-explanation"
              >
                {declineCopy.explanation}
              </Typography>
            ) : declineCopy.code ? (
              <Typography variant="body2" sx={{ lineHeight: 1.45, color: 'text.primary', opacity: 0.85 }}>
                Vendor declined with code {declineCopy.code}. No further explanation was returned.
              </Typography>
            ) : null}
            <Typography
              variant="caption"
              display="block"
              sx={{ mt: 0.75, lineHeight: 1.4, color: 'text.primary', fontWeight: 500, opacity: 0.78 }}
            >
              Hard decline for this application — capacity is not offered. Customer sees a soft
              unable-to-offer message.
            </Typography>
          </Box>
        ) : declined && !isPdl ? (
          <Typography variant="body2" sx={{ lineHeight: 1.5, color: 'text.primary', opacity: 0.85 }}>
            Hard decline — capacity is not offered for this application.
          </Typography>
        ) : null}

        {hasEvaluation || (isPdl && applicationDeclineReason) ? (
          <>
            {isPdl ? (
              <PdlAdjudicatorBrief
                decisionEngine={decisionEngine}
                applicationDeclineReason={applicationDeclineReason}
                declineCode={declineCopy.code}
                evidence={pdlEvidence}
                evidenceLoading={incomeDetailQ.isLoading && Boolean(latestIncomeId)}
                onNavigate={onNavigate}
              />
            ) : null}

            {!isPdl ? (
            <Box
              sx={{
                display: 'grid',
                gap: 1.5,
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                alignItems: 'stretch',
              }}
            >
              <KvSection
                title="Capacity outcome"
                subtitle={
                  declined
                    ? 'No loan capacity after Decision Engine decline'
                    : 'Capacity offered to the journey'
                }
                rows={capacityRows}
              />
              <KvSection
                title="Capacity logic inputs"
                subtitle="Ops product table · py-model · DTI lending"
                rows={logicRows}
              />
            </Box>
            ) : null}

            {!isPdl ? (
              <Box
                sx={{
                  display: 'grid',
                  gap: 1.5,
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                  alignItems: 'stretch',
                }}
              >
                <KvSection title="DTI & affordability" rows={dtiRows} />
                <KvSection title="TransUnion output" rows={tuRows} />
              </Box>
            ) : null}

            {!isPdl ? <KvSection title="Addresses (TU)" rows={addressRows} /> : null}

            {isPdl && (pdlTraceRowCount > 0 || showPdlElodDetail) ? (
              <Accordion
                expanded={pdlTechnicalOpen}
                onChange={(_, expanded) => setPdlTechnicalOpen(expanded)}
                disableGutters
                elevation={0}
                data-testid="application-decision-engine-pdl-technical"
                data-tour-target="application-decision-engine-pdl-technical"
                sx={{
                  borderRadius: '12px !important',
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreRoundedIcon />}
                  sx={{
                    px: 1.5,
                    minHeight: 48,
                    '& .MuiAccordionSummary-content': { my: 1, alignItems: 'center', gap: 1 },
                  }}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 800,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        color: 'text.secondary',
                        fontSize: '0.65rem',
                        display: 'block',
                      }}
                    >
                      Technical details
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ lineHeight: 1.45, mt: 0.25 }}
                    >
                      Full trace for audit — skip for routine review (use the guided chart above).
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 1.25 }}>
                  <Stack spacing={1.5}>
                    {showPdlElodDetail ? (
                      <KvSection
                        title="ELOD risk signals"
                        subtitle="Bank-derived existing loan exposure"
                        rows={pdlElodRows}
                      />
                    ) : null}
                    {pdlTraceRowCount > 0 ? (
                      <PdlTechnicalTracePanel sections={pdlTraceSections} />
                    ) : null}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            ) : null}

            {isPdl && pdlRemarkGroups.length > 0 ? (
              <Accordion
                expanded={pdlRemarksOpen}
                onChange={(_, expanded) => setPdlRemarksOpen(expanded)}
                disableGutters
                elevation={0}
                data-testid="application-decision-engine-pdl-remarks"
                data-tour-target="application-decision-engine-pdl-remarks"
                sx={{
                  borderRadius: '12px !important',
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreRoundedIcon />}
                  sx={{
                    px: 1.5,
                    minHeight: 48,
                    '& .MuiAccordionSummary-content': { my: 1, alignItems: 'center', gap: 1 },
                  }}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 800,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        color: 'text.secondary',
                        fontSize: '0.65rem',
                        display: 'block',
                      }}
                    >
                      Policy signals & factors
                    </Typography>
                    {!pdlRemarksOpen ? (
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{ lineHeight: 1.45, opacity: 0.72, mt: 0.25 }}
                      >
                        Regulatory & MBM notes — informational only; not needed for amount review.
                      </Typography>
                    ) : null}
                  </Box>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`${pdlRemarkCards.length} signal${pdlRemarkCards.length === 1 ? '' : 's'}`}
                    sx={{ height: 22, fontWeight: 700, flexShrink: 0 }}
                  />
                </AccordionSummary>
                <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 1.25 }}>
                  <Typography
                    variant="caption"
                    display="block"
                    sx={{ mb: 1, lineHeight: 1.45, color: 'text.secondary' }}
                  >
                    Plain-language policy notes. Only items marked “Set the offer limit” change the approved amount.
                  </Typography>
                  <PdlPolicySignalsPanel groups={pdlRemarkGroups} />
                </AccordionDetails>
              </Accordion>
            ) : null}

            {existingLoans.length > 0 ? (
              <Box
                sx={{
                  borderRadius: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                  px: 1.5,
                  py: 1.25,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 800,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: 'text.secondary',
                    fontSize: '0.65rem',
                    display: 'block',
                    mb: 1,
                  }}
                >
                  Existing loan details (TU trades)
                </Typography>
                <Box
                  sx={{
                    borderRadius: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    overflow: 'hidden',
                  }}
                >
                  <Box sx={{ overflowX: 'auto' }}>
                    <Box
                      component="table"
                      sx={{
                        width: '100%',
                        minWidth: 520,
                        borderCollapse: 'collapse',
                      }}
                    >
                      <Box
                        component="thead"
                        sx={{
                          bgcolor:
                            theme.palette.mode === 'light'
                              ? alpha(theme.palette.common.black, 0.02)
                              : alpha(theme.palette.common.white, 0.04),
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        <Box component="tr">
                          <TableHeaderCell>Creditor</TableHeaderCell>
                          <TableHeaderCell>Type</TableHeaderCell>
                          <TableHeaderCell align="right">Balance</TableHeaderCell>
                          <TableHeaderCell align="right">Payment</TableHeaderCell>
                          <TableHeaderCell>Status</TableHeaderCell>
                          <TableHeaderCell>Opened</TableHeaderCell>
                        </Box>
                      </Box>
                      <Box component="tbody">
                        {existingLoans.map((loan, idx) => (
                          <Box
                            component="tr"
                            key={`${loan.memberName ?? 'loan'}-${idx}`}
                            sx={{
                              borderBottom:
                                idx < existingLoans.length - 1 ? '1px solid' : undefined,
                              borderColor: 'divider',
                              '&:hover': {
                                bgcolor: alpha(
                                  theme.palette.common.black,
                                  theme.palette.mode === 'light' ? 0.02 : 0.06,
                                ),
                              },
                            }}
                          >
                            {[
                              loan.memberName ?? '—',
                              loan.accountType ?? '—',
                              formatMoney(loan.balance) ?? '—',
                              formatMoney(loan.payment) ?? '—',
                              formatTuStatus(loan.status),
                              formatTuDate(loan.dateOpened),
                            ].map((cell, cellIdx) => (
                              <Typography
                                key={cellIdx}
                                component="td"
                                variant="body2"
                                sx={{
                                  py: 1,
                                  px: 1.25,
                                  fontWeight: 500,
                                  fontSize: '0.875rem',
                                  textAlign: cellIdx === 2 || cellIdx === 3 ? 'right' : 'left',
                                  whiteSpace: 'nowrap',
                                  fontVariantNumeric: 'tabular-nums',
                                }}
                              >
                                {cell}
                              </Typography>
                            ))}
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            ) : null}
          </>
        ) : null}
      </Stack>
    </ApplicationSectionCard>
  );
}
